import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import classNames from "classnames/bind";
import { IconButton, Image, YoutubeThumbnail } from "@components";
import { AcfMediaFragment } from "@/__generated__/graphql";
import ArrowRightIcon from "@/assets/svg/arrow-right.svg";
import YoutubeEmbed, { YTRef } from "./YoutubeEmbed";
import { PreviewVideoRef } from "./YoutubeVideoPreview";
import { checkYoutubeIframeApi } from "@/core/utils";

import styles from "./YoutubeEmbed.module.scss";
const cx = classNames.bind(styles);

type YoutubeImagePreviewProps = {
  id: string;
  thumbnail: AcfMediaFragment;
  className?: string;
  playVideoLabel?: string;
  consentSmall?: boolean;
}

const YoutubeImagePreview = forwardRef<PreviewVideoRef, YoutubeImagePreviewProps>(({ id, thumbnail, className, playVideoLabel, consentSmall }, ref) => {
  const [showVideo, setShowVideo] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YTRef>(null);
  const onClick = () => { setShowVideo(true); };
  useEffect(() => { checkYoutubeIframeApi(); }, []);
  useImperativeHandle(ref, () => ({
    getPlayer: () => playerRef.current?.getPlayer(),
    showEmbed: () => setShowVideo(true),
  }));

  // handle parent's click
  useEffect(() => {
    if (!rootRef.current) return;
    const parent = rootRef.current.closest('button');
    if (parent) {
      parent.addEventListener('click', onClick);
    }
    return () => {
      if (parent) {
        parent.removeEventListener('click', onClick);
      }
    };
  }, []);

  return (
    <div className={cx(["youtube-image-preview", className])} onClick={onClick} ref={rootRef}>
      <span className={cx("overlay")} />
      { thumbnail
        ? <Image image={thumbnail} className={cx("image")} />
        : <YoutubeThumbnail className={cx("image")} youtubeId={id} />
      }
      <IconButton
        className={cx("play-button")}
        icon={ArrowRightIcon}
        size="large"
        ariaLabel={`Play video - ${playVideoLabel || ''}`}
        tag="div"
      />
      { showVideo && (
        <YoutubeEmbed
          id={id}
          ref={playerRef}
          consentSmall={consentSmall}
          onConsentedElsewhere={() => setShowVideo(false)}
        />
      )}
    </div>
  );
});

export default YoutubeImagePreview;